import { FC, useLayoutEffect, useMemo, useState } from 'react';

import SubmitButton from '@/components/elements/Button/BaseButton';
import Input from '@/components/elements/Form/Input';
import Select, { Option } from '@/components/elements/Form/Select';
import { prefectures } from '@/lib/address';
import {
  GradeUnitCode,
  SearchEntryRacerInputParam,
} from '@/lib/apollo/types/gen/graphql';

import {
  AccordionButton,
  AccordionCloseIcon,
  AccordionWrapper,
  ButtonMessageWrapper,
  ButtonWrapper,
  ConditionItem,
  ConditionList,
  Container,
  DetailContainer,
  DetailWrapper,
  ErrorNote,
  Headline,
  InputWrapper,
} from '../style';

interface SearchRacerConditionTemplateProps {
  loading: boolean;
  openCondition: boolean;
  defaultParam?: SearchEntryRacerInputParam;
  setOpenCondition: (val: boolean) => void;
  viewMatchDetail: (
    argName: string,
    argPrefecture: string,
    argAge: number,
    argGraduated: string,
    argGuCode?: GradeUnitCode,
  ) => void;
}

const SearchRacerConditionTemplate: FC<SearchRacerConditionTemplateProps> = ({
  loading,
  openCondition,
  defaultParam,
  setOpenCondition,
  viewMatchDetail,
}) => {
  const [conditionName, setConditionName] = useState<string>('');
  const [conditionPref, setConditionPref] = useState<string>('');
  const [conditionAge, setConditionAge] = useState<number>(0);
  const [conditionGuCode, setConditionGuCode] = useState<string>('');
  const [conditionGrad, setConditionGrad] = useState<string>('');

  const prefectureOptions = useMemo(() => {
    const options: Option[] = [];
    options.push({ value: '', displayName: '（指定なし）' }); // 一番上に「全て」を追加
    prefectures.map(p => {
      options.push({
        value: p.name
          .replace('東京都', '東京')
          .replace('道', '')
          .replace('府', '')
          .replace('県', ''),
        displayName: p.name,
      });
    });
    return options;
  }, []);

  const ageOptions = useMemo(() => {
    const options: Option[] = [];
    options.push({ value: 0, displayName: '（指定なし）' });
    options.push({ value: 10, displayName: '10代' });
    options.push({ value: 20, displayName: '20代' });
    options.push({ value: 30, displayName: '30代' });
    options.push({ value: 40, displayName: '40代' });
    options.push({ value: 50, displayName: '50代' });
    options.push({ value: 60, displayName: '60代以上' });
    return options;
  }, []);

  const guCodeOptions = useMemo(() => {
    const options: Option[] = [];
    options.push({ value: '', displayName: '（指定なし）' });
    options.push({ value: GradeUnitCode.Ss.toString(), displayName: 'S級S班' });
    options.push({ value: GradeUnitCode.S1.toString(), displayName: 'S級1班' });
    options.push({ value: GradeUnitCode.S2.toString(), displayName: 'S級2班' });
    options.push({ value: GradeUnitCode.A1.toString(), displayName: 'A級1班' });
    options.push({ value: GradeUnitCode.A2.toString(), displayName: 'A級2班' });
    options.push({ value: GradeUnitCode.A3.toString(), displayName: 'A級3班' });
    options.push({ value: GradeUnitCode.L1.toString(), displayName: 'L級1班' });
    return options;
  }, []);

  const isDisabledButton = useMemo(() => {
    if (
      conditionName == '' &&
      conditionPref == '' &&
      conditionAge == 0 &&
      conditionGuCode == '' &&
      conditionGrad == ''
    ) {
      // 検索条件が設定されていない場合は、Disabled
      return true;
    }
  }, [
    conditionName,
    conditionPref,
    conditionAge,
    conditionGuCode,
    conditionGrad,
  ]);

  const convertGradeUnitCode = (val: string) => {
    switch (val) {
      case GradeUnitCode.Ss.toString():
        return GradeUnitCode.Ss;
      case GradeUnitCode.S1.toString():
        return GradeUnitCode.S1;
      case GradeUnitCode.S2.toString():
        return GradeUnitCode.S2;
      case GradeUnitCode.A1.toString():
        return GradeUnitCode.A1;
      case GradeUnitCode.A2.toString():
        return GradeUnitCode.A2;
      case GradeUnitCode.A3.toString():
        return GradeUnitCode.A3;
      case GradeUnitCode.L1.toString():
        return GradeUnitCode.L1;
    }
    return undefined;
  };

  const clickSearch = () => {
    viewMatchDetail(
      conditionName,
      conditionPref,
      conditionAge,
      conditionGrad,
      convertGradeUnitCode(conditionGuCode),
    );
    setOpenCondition(false);
  };

  useLayoutEffect(() => {
    if (defaultParam) {
      setConditionName(defaultParam.racerName);
      setConditionPref(defaultParam.prefecture);
      setConditionAge(defaultParam.ageRange);
      setConditionGrad(defaultParam.graduated);
      if (defaultParam.gradeUnitCode) {
        setConditionGuCode(defaultParam.gradeUnitCode.toString());
      }
    }
  }, [defaultParam]);

  return (
    <Container>
      <Headline>選手検索</Headline>
      <DetailWrapper>
        <DetailContainer>
          <AccordionWrapper isOpen={openCondition}>
            <ConditionList>
              <ConditionItem>
                <dt>名前</dt>
                <dd>
                  <InputWrapper>
                    <Input
                      type="text"
                      placeholder=""
                      maxLength={6}
                      autoComplete="off"
                      height="48px"
                      fontSize="m"
                      bgColor="lightGray"
                      value={conditionName}
                      onChange={e => setConditionName(e.target.value)}
                    />
                  </InputWrapper>
                </dd>
              </ConditionItem>
              <ConditionItem>
                <dt>都道府県</dt>
                <dd>
                  <InputWrapper>
                    <Select
                      radius="s"
                      options={prefectureOptions}
                      value={conditionPref}
                      width="110px"
                      onChange={e => setConditionPref(e.target.value)}
                    />
                  </InputWrapper>
                </dd>
              </ConditionItem>
              <ConditionItem>
                <dt>年齢</dt>
                <dd>
                  <InputWrapper>
                    <Select
                      radius="s"
                      options={ageOptions}
                      value={conditionAge}
                      width="110px"
                      onChange={e => setConditionAge(Number(e.target.value))}
                    />
                  </InputWrapper>
                </dd>
              </ConditionItem>
              <ConditionItem>
                <dt>級班</dt>
                <dd>
                  <InputWrapper>
                    <Select
                      radius="s"
                      options={guCodeOptions}
                      value={conditionGuCode}
                      width="110px"
                      onChange={e => setConditionGuCode(e.target.value)}
                    />
                  </InputWrapper>
                </dd>
              </ConditionItem>
              <ConditionItem>
                <dt>卒業期</dt>
                <dd>
                  <InputWrapper>
                    <Input
                      type="text"
                      placeholder=""
                      maxLength={3}
                      autoComplete="off"
                      height="48px"
                      fontSize="m"
                      bgColor="lightGray"
                      value={conditionGrad}
                      onChange={e => setConditionGrad(e.target.value)}
                    />
                  </InputWrapper>
                </dd>
              </ConditionItem>
            </ConditionList>

            <ButtonWrapper>
              <SubmitButton
                type="button"
                outline={true}
                bgColor="black"
                size="m"
                fontWeight="bold"
                radius="l"
                height="100%"
                disabled={loading || isDisabledButton}
                onClick={() => {
                  clickSearch();
                }}
              >
                検索する
              </SubmitButton>
            </ButtonWrapper>
            {isDisabledButton && (
              <ButtonMessageWrapper>
                <ErrorNote>検索条件を1つ以上指定してください</ErrorNote>
              </ButtonMessageWrapper>
            )}
          </AccordionWrapper>

          <AccordionButton
            onClick={() => setOpenCondition(!openCondition)}
            isOpen={openCondition}
          >
            {!openCondition && <span>検索条件を変更する</span>}
            <AccordionCloseIcon isOpen={openCondition} />
          </AccordionButton>
        </DetailContainer>
      </DetailWrapper>
    </Container>
  );
};

export default SearchRacerConditionTemplate;
